import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Cog6ToothIcon, CheckIcon } from '@heroicons/react/24/outline';

interface Setting {
  id: string;
  category: string;
  key: string;
  value: string | null;
  isSecret: boolean;
  description: string | null;
  updatedAt: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  system: 'System Settings',
  analysis: 'Analysis Settings',
  notifications: 'Notifications',
};

export default function AdminSettings() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/v1/admin/settings`, { headers });
      // Filter out API credentials (handled in separate page)
      const filtered = response.data.settings.filter(
        (s: Setting) => s.category !== 'api_credentials'
      );
      setSettings(filtered);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingKey(`${setting.category}/${setting.key}`);
    setEditValue(setting.value || '');
  };

  const handleSave = async (setting: Setting) => {
    setSaving(true);
    try {
      await axios.put(
        `${baseUrl}/api/v1/admin/settings/${setting.category}/${setting.key}`,
        { value: editValue },
        { headers }
      );
      toast.success('Setting saved successfully');
      setEditingKey(null);
      fetchSettings();
    } catch (error) {
      console.error('Failed to save setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  // Group settings by category
  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {} as Record<string, Setting[]>);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-500 mt-1">Configure system behavior and defaults</p>
      </div>

      {Object.entries(groupedSettings).map(([category, categorySettings]) => (
        <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center">
              <Cog6ToothIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                {CATEGORY_LABELS[category] || category}
              </h2>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {categorySettings.map((setting) => {
              const editKey = `${setting.category}/${setting.key}`;
              const isEditing = editingKey === editKey;

              return (
                <div key={editKey} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <label className="block text-sm font-medium text-gray-900">
                        {setting.key
                          .split('_')
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(' ')}
                      </label>
                      {setting.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{setting.description}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-40 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                          <button
                            onClick={() => handleSave(setting)}
                            disabled={saving}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm font-mono text-gray-600 bg-gray-100 px-3 py-1.5 rounded">
                            {setting.value || 'Not set'}
                          </span>
                          <button
                            onClick={() => handleEdit(setting)}
                            className="px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {settings.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No system settings available</p>
          <p className="text-sm text-gray-400 mt-1">
            Settings will appear here after system initialization
          </p>
        </div>
      )}
    </div>
  );
}
